// src/pages/OrderOnTheWay/PaymentsToBePaid.jsx
import React, { useState, useEffect, useMemo } from "react";
import Layout from "../component/Layout";
import PaymentPendingList from "./components/PaymentPendingList";
import PaymentDeliveredList from "./components/PaymentDeliveredList";
import JmiCommissionPayModal, {
  getFridayCycle,
} from "./components/JmiCommissionPayModal";
import { useOrderGroups } from "./hooks/useOrderGroups";
import { useOperator } from "../contexts/OperaotrContext";
import { calculateFourPartyPricing } from "./utils/calculationutil";
import { fetchIndiaBaseRates } from "../services/stateRates";
import {
  SettlementKpis,
  SettlementTable,
  buildReconciliation,
} from "../components/SettlementReconciliation";
import { Landmark, Calendar, ShieldCheck } from "lucide-react";

const PaymentsToBePaid = () => {
  const [activeTab, setActiveTab] = useState("pending");
  const operatorId = localStorage.getItem("operatorId");
  const {
    pendingGroups,
    onwayGroups,
    deliveredGroups,
    loading,
    alertMessage,
    setAlertMessage,
  } = useOrderGroups(operatorId);
  const [rtgsSaleRate, setRtgsSaleRate] = useState(0);
  const [ratesLoading, setRatesLoading] = useState(true);
  const [showJmiPayModal, setShowJmiPayModal] = useState(false);

  const fridayCycle = useMemo(() => getFridayCycle(), []);

  // Fetch Live RTGS Sale Rate
  useEffect(() => {
    let cancelled = false;
    const loadRates = async () => {
      setRatesLoading(true);
      try {
        const baseRates = await fetchIndiaBaseRates();
        const goldRate = baseRates?.gold?.rtgsSale || 0;
        if (!cancelled) {
          setRtgsSaleRate(goldRate);
        }
      } catch (err) {
        console.error("Failed to load live rates for JMI top card:", err);
      } finally {
        if (!cancelled) setRatesLoading(false);
      }
    };
    loadRates();
    return () => {
      cancelled = true;
    };
  }, []);

  // Flatten all delivered orders
  const allDeliveredOrders = useMemo(() => {
    return Object.values(deliveredGroups).flatMap((group) => group.orders);
  }, [deliveredGroups]);
  // Four-party reconciliation for every settled order:
  // buyer paid vs seller paid vs operator margin vs JMI commission.
  const reconRows = useMemo(
    () => allDeliveredOrders.map((o) => buildReconciliation(o, rtgsSaleRate)),
    [allDeliveredOrders, rtgsSaleRate],
  );
  // Delivered orders that haven't been cleared for JMI yet
  const pendingJmiDeliveredOrders = useMemo(() => {
    return allDeliveredOrders.filter(
      (order) => !order.jmiSettled && !order.jmiCommissionSettled,
    );
  }, [allDeliveredOrders]);

  // Total order count
  const getTotalOrderCount = () => {
    const pendingCount = Object.values(pendingGroups).reduce(
      (total, group) => total + group.orders.length,
      0,
    );
    const onwayCount = Object.values(onwayGroups).reduce(
      (total, group) => total + group.orders.length,
      0,
    );
    const deliveredCount = allDeliveredOrders.length;
    return pendingCount + onwayCount + deliveredCount;
  };
  // Calculations for Delivered top card — uses same per-order calc as table (calculateFourPartyPricing)
  // so the card sum matches the table total exactly
  const activeJmiTotal = useMemo(() => {
    let fineWeight = 0;
    let fineAmount = 0;
    let totalMc = 0;
    let totalCommission = 0;
    const rate = parseFloat(rtgsSaleRate) || 0;
    if (activeTab === "delivered") {
      allDeliveredOrders.forEach((order) => {
        const { jmi } = calculateFourPartyPricing(order, rate);
        fineWeight += parseFloat(jmi.fineWeight) || 0;
        fineAmount += parseFloat(jmi.fineAmount) || 0;
        totalMc += parseFloat(jmi.totalMc) || 0;
        totalCommission += parseFloat(jmi.totalCommission) || 0;
      });
    } else {
      const allOrders = [
        ...Object.values(pendingGroups).flatMap((g) => g.orders),
        ...Object.values(onwayGroups).flatMap((g) => g.orders),
        ...allDeliveredOrders,
      ];
      allOrders.forEach((order) => {
        const { jmi } = calculateFourPartyPricing(order, rate);
        fineWeight += parseFloat(jmi.fineWeight) || 0;
        fineAmount += parseFloat(jmi.fineAmount) || 0;
        totalMc += parseFloat(jmi.totalMc) || 0;
        totalCommission += parseFloat(jmi.totalCommission) || 0;
      });
    }
    return { fineWeight, fineAmount, totalMc, totalCommission };
  }, [activeTab, pendingGroups, onwayGroups, allDeliveredOrders, rtgsSaleRate]);

  const activeJmiFine = activeJmiTotal.fineWeight;
  const activeJmiMcAmount = activeJmiTotal.totalMc;
  const totalJmiFineAmount = activeJmiTotal.fineAmount;
  const grandTotalJmiCommission = activeJmiTotal.totalCommission;
  // Delivered pending JMI fine & amount for PayModal
  // Pending JMI settlement for PayModal (uses per-order calc for consistency with table)
  const pendingJmiSettlement = useMemo(() => {
    let fine = 0;
    let amount = 0;
    const rate = parseFloat(rtgsSaleRate) || 0;
    pendingJmiDeliveredOrders.forEach((order) => {
      const { jmi } = calculateFourPartyPricing(order, rate);
      fine += parseFloat(jmi.fineWeight) || 0;
      amount += parseFloat(jmi.totalMc) || 0;
    });
    return { fine, amount };
  }, [pendingJmiDeliveredOrders, rtgsSaleRate]);

  const tabStyle = {
    display: "flex",
    borderBottom: "1px solid #d1d5db",
    marginBottom: "20px",
    backgroundColor: "#fff",
  };

  const tabButtonStyle = (isActive) => ({
    padding: "12px 24px",
    border: "none",
    background: "none",
    cursor: "pointer",
    borderBottom: `3px solid ${isActive ? "#4f46e5" : "transparent"}`,
    fontWeight: "600",
    color: isActive ? "#4f46e5" : "#6b7280",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  });

  const alertStyle = {
    backgroundColor: "#d1fae5",
    color: "#065f46",
    padding: "12px 16px",
    textAlign: "center",
    marginBottom: "16px",
    borderRadius: "8px",
    fontWeight: "bold",
    border: "1px solid #a7f3d0",
  };

  const jmiSummaryStyle = {
    background:
      "linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)",
    borderRadius: "14px",
    padding: "24px",
    marginBottom: "24px",
    color: "white",
    boxShadow: "0 10px 25px -5px rgba(49, 46, 129, 0.25)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
  };

  const summaryHeaderStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "12px",
    marginBottom: "18px",
  };

  const summaryTitleStyle = {
    margin: 0,
    color: "white",
    fontSize: "20px",
    fontWeight: "700",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  const cycleBadgeStyle = {
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(8px)",
    padding: "6px 12px",
    borderRadius: "20px",
    fontSize: "12px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    border: "1px solid rgba(255, 255, 255, 0.25)",
  };


  const settlementCardStyle = {
    background: "rgba(255, 255, 255, 0.08)",
    backdropFilter: "blur(10px)",
    padding: "16px",
    borderRadius: "10px",
    border: "1px solid rgba(255, 255, 255, 0.15)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  };

  const highlightCardStyle = {
    ...settlementCardStyle,
    background: "rgba(255, 255, 255, 0.18)",
    border: "1px solid rgba(255, 255, 255, 0.35)",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  };

  const labelStyle = {
    display: "block",
    color: "rgba(255, 255, 255, 0.75)",
    fontSize: "12px",
    marginBottom: "6px",
    fontWeight: "500",
  };

  const valueStyle = {
    display: "block",
    color: "white",
    fontSize: "20px",
    fontWeight: "700",
  };

  return (
    <Layout pageTitle="Payments Management">
      {alertMessage && <div style={alertStyle}>{alertMessage}</div>}

      {/* JMI Settlement Summary Card with RTGS Sale Rate Calculation & Friday Cycle */}
      <div style={jmiSummaryStyle}>
        <div style={summaryHeaderStyle}>
          <h3 style={summaryTitleStyle}>
            <Landmark size={22} />
            JMI service-fee Summary (
            {activeTab === "delivered" ? "Orders to Settle" : "All Orders"})
          </h3>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              flexWrap: "wrap",
            }}
          >
            <div style={cycleBadgeStyle}>
              <Calendar size={14} />
              <span>{fridayCycle.cycleLabel}</span>
            </div>
            {activeTab === "delivered" && (
              <button
                onClick={() => setShowJmiPayModal(true)}
                disabled={pendingJmiDeliveredOrders.length === 0}
                style={{
                  backgroundColor:
                    pendingJmiDeliveredOrders.length > 0
                      ? "#10b981"
                      : "rgba(255, 255, 255, 0.2)",
                  color: "white",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "8px",
                  fontWeight: "600",
                  fontSize: "13px",
                  cursor:
                    pendingJmiDeliveredOrders.length > 0
                      ? "pointer"
                      : "not-allowed",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
                  transition: "all 0.2s ease",
                }}
              >
                <ShieldCheck size={16} />
                💳 JMI Service-Fee ({pendingJmiDeliveredOrders.length} Due)
              </button>
            )}
          </div>
        </div>

        {/* 3 Top Summary Cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: "16px",
          }}
        >
          {/* Card 1: Total Orders */}
          <div style={settlementCardStyle}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "8px",
              }}
            >
              <span style={labelStyle}>Total Orders</span>
              <span
                style={{
                  fontSize: "11px",
                  backgroundColor: "rgba(255,255,255,0.15)",
                  padding: "2px 8px",
                  borderRadius: "12px",
                }}
              >
                {activeTab === "delivered" ? "Delivered" : "All Orders"}
              </span>
            </div>
            <span style={{ ...valueStyle, fontSize: "28px" }}>
              {activeTab === "delivered"
                ? allDeliveredOrders.length
                : getTotalOrderCount()}
            </span>
            <span
              style={{
                fontSize: "12px",
                color: "rgba(255,255,255,0.7)",
                marginTop: "4px",
              }}
            >
              Delivered: {allDeliveredOrders.length} · Pending JMI:{" "}
              {pendingJmiDeliveredOrders.length}
            </span>
          </div>

          {/* Card 2: Total JMI Fine */}
          <div style={settlementCardStyle}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "8px",
              }}
            >
              <span style={labelStyle}>JMI Value (Fine)</span>
              <span
                style={{
                  fontSize: "11px",
                  color: "#34d399",
                  fontWeight: "600",
                }}
              >
                {ratesLoading
                  ? "Loading…"
                  : `@ ₹${rtgsSaleRate.toLocaleString("en-IN")}/g`}
              </span>
            </div>
            <span style={{ ...valueStyle, fontSize: "28px", color: "#fbbf24" }}>
              {activeJmiFine.toFixed(3)}g
            </span>
            <span
              style={{ fontSize: "12px", color: "#a7f3d0", marginTop: "4px" }}
            >
              Fine Amount: ₹
              {totalJmiFineAmount.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>

          {/* Card 3: Total Amount (To be paid) */}
          <div style={highlightCardStyle}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "8px",
              }}
            >
              <span
                style={{ ...labelStyle, color: "#fef08a", fontWeight: "600" }}
              >
                Total Amount (To be Paid)
              </span>
              <span
                style={{
                  fontSize: "11px",
                  backgroundColor: "#fef08a",
                  color: "#1e1b4b",
                  fontWeight: "700",
                  padding: "2px 8px",
                  borderRadius: "12px",
                }}
              >
                service-fee
              </span>
            </div>
            <span style={{ ...valueStyle, fontSize: "28px", color: "#fef08a" }}>
              ₹
              {grandTotalJmiCommission.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
            <span
              style={{
                fontSize: "12px",
                color: "rgba(254, 240, 138, 0.85)",
                marginTop: "4px",
              }}
            >
              Fine ₹{totalJmiFineAmount.toFixed(2)} + MC ₹
              {activeJmiMcAmount.toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      {/* Settlement reconciliation dashboard */}
      <div
        style={{
          border: "1px solid #e2e8f0",
          borderRadius: 12,
          background: "#f8fafc",
          padding: 14,
          marginBottom: 18,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "#8a6b32",
            marginBottom: 10,
          }}
        >
          Settlement Reconciliation — Buyer vs Seller vs Operator vs JMI
        </div>
        <SettlementKpis rows={reconRows} rate={rtgsSaleRate} />
        <SettlementTable rows={reconRows} />
      </div>

      {/* Tab Navigation */}
      <div style={tabStyle}>
        <button
          style={tabButtonStyle(activeTab === "pending")}
          onClick={() => setActiveTab("pending")}
        >
          📦 Pending ({Object.keys(pendingGroups).length})
        </button>
        <button
          style={tabButtonStyle(activeTab === "onway")}
          onClick={() => setActiveTab("onway")}
        >
          🚚 On the Way ({Object.keys(onwayGroups).length})
        </button>
        <button
          style={tabButtonStyle(activeTab === "delivered")}
          onClick={() => setActiveTab("delivered")}
        >
          ✅ Delivered / Payment Settled ({Object.keys(deliveredGroups).length})
        </button>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === "pending" && (
          <PaymentPendingList
            groupedOrders={pendingGroups}
            loading={loading}
            setAlertMessage={setAlertMessage}
          />
        )}
        {activeTab === "onway" && (
          <PaymentPendingList
            groupedOrders={onwayGroups}
            loading={loading}
            setAlertMessage={setAlertMessage}
          />
        )}
        {activeTab === "delivered" && (
          <PaymentDeliveredList
            groupedOrders={deliveredGroups}
            loading={loading}
            setAlertMessage={setAlertMessage}
            rtgsSaleRate={rtgsSaleRate}
            onOpenJmiPayModal={() => setShowJmiPayModal(true)}
          />
        )}
      </div>

      {/* JMI Commission Pay Modal for Friday Clearance */}
      {showJmiPayModal && (
        <JmiCommissionPayModal
          orders={
            pendingJmiDeliveredOrders.length > 0
              ? pendingJmiDeliveredOrders
              : allDeliveredOrders
          }
          rtgsSaleRate={rtgsSaleRate}
          totalJmiFine={
            pendingJmiDeliveredOrders.length > 0
              ? pendingJmiSettlement.fine
              : activeJmiTotal.fineWeight
          }
          totalJmiAmount={
            pendingJmiDeliveredOrders.length > 0
              ? pendingJmiSettlement.amount
              : activeJmiTotal.totalMc
          }
          onClose={() => setShowJmiPayModal(false)}
          onSuccess={(msg) => {
            setAlertMessage(msg);
            setShowJmiPayModal(false);
          }}
        />
      )}
    </Layout>
  );
};

export default PaymentsToBePaid;
